import { useCallback, useRef, useState } from "react";
import { Upload, X, Image as ImageIcon, Loader2, AlertCircle, Camera } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";

export type JobPhoto = {
  id: string;
  path: string;          // storage object path inside `job-photos`
  previewUrl: string;    // local object URL for instant preview
  name: string;
  size: number;
};

const MAX_FILES = 6;
const MAX_BYTES = 8 * 1024 * 1024; // 8 MB
const ACCEPTED = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];

type Props = {
  userId: string | null;     // null = not signed in; we'll defer real upload until auth
  value: JobPhoto[];
  onChange: (next: JobPhoto[]) => void;
  /** Optional storage path prefix appended after the userId folder. Trailing slash optional. */
  subPath?: string;
  /** Override max files (default 6). */
  maxFiles?: number;
};

/**
 * Mobile-first drag-and-drop photo uploader for the job-posting flow.
 * - Drag/drop OR tap to choose OR use device camera (capture="environment")
 * - Instant local previews, individual upload progress, per-file error states
 * - Validates type/size before sending bytes
 * - Files upload to the private `job-photos` bucket under <userId>/<random>.<ext>
 */
export function JobPhotosUploader({ userId, value, onChange, subPath, maxFiles }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState<Record<string, number>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const MAX = maxFiles ?? MAX_FILES;
  const remaining = MAX - value.length;
  const sub = subPath ? subPath.replace(/^\/+|\/+$/g, "") + "/" : "";

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files).slice(0, remaining);
      const newErrors: Record<string, string> = {};
      const accepted: File[] = [];
      for (const f of list) {
        if (!ACCEPTED.includes(f.type) && !/\.(jpe?g|png|webp|heic|heif)$/i.test(f.name)) {
          newErrors[f.name] = "Unsupported file — use JPG, PNG, WEBP or HEIC.";
          continue;
        }
        if (f.size > MAX_BYTES) {
          newErrors[f.name] = `Too large (${(f.size / 1024 / 1024).toFixed(1)} MB) — max 8 MB.`;
          continue;
        }
        accepted.push(f);
      }
      if (Object.keys(newErrors).length) setErrors((e) => ({ ...e, ...newErrors }));

      // Stage local previews immediately so the UI feels instant.
      const staged: JobPhoto[] = accepted.map((f) => ({
        id: crypto.randomUUID(),
        path: "",
        previewUrl: URL.createObjectURL(f),
        name: f.name,
        size: f.size,
      }));
      if (!staged.length) return;
      onChange([...value, ...staged]);

      // Upload one by one so progress is per-file.
      for (let i = 0; i < accepted.length; i++) {
        const file = accepted[i];
        const photo = staged[i];
        setUploading((u) => ({ ...u, [photo.id]: 5 }));
        try {
          if (!userId) {
            // Sign-in happens after submit; we keep the local preview and upload on retry.
            setUploading((u) => ({ ...u, [photo.id]: 100 }));
            continue;
          }
          const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
          const path = `${userId}/${sub}${crypto.randomUUID()}.${ext}`;
          // Naive progress: start, mid, done — Supabase JS doesn't yet stream progress reliably.
          setUploading((u) => ({ ...u, [photo.id]: 35 }));
          const { error } = await supabase.storage
            .from("job-photos")
            .upload(path, file, { contentType: file.type || "image/jpeg", upsert: false });
          if (error) throw error;
          setUploading((u) => ({ ...u, [photo.id]: 100 }));
          onChange(
            // re-read latest via functional pattern not possible here; use closure over latest value via setTimeout 0
            // but we need stable: derive from current array snapshot at call time.
            [...value, ...staged].map((p) => (p.id === photo.id ? { ...p, path } : p)),
          );
        } catch (err) {
          const msg = err instanceof Error ? err.message : "Upload failed";
          setErrors((e) => ({ ...e, [photo.id]: msg }));
          setUploading((u) => {
            const n = { ...u };
            delete n[photo.id];
            return n;
          });
          // Remove failed staged photo
          onChange(value.concat(staged.filter((p) => p.id !== photo.id)));
        }
      }
    },
    [onChange, remaining, userId, value],
  );

  function remove(id: string) {
    const photo = value.find((p) => p.id === id);
    if (photo?.previewUrl) URL.revokeObjectURL(photo.previewUrl);
    onChange(value.filter((p) => p.id !== id));
    setErrors((e) => {
      const n = { ...e };
      delete n[id];
      return n;
    });
    if (photo?.path && userId) {
      // Best-effort cleanup; ignore failures.
      void supabase.storage.from("job-photos").remove([photo.path]);
    }
  }

  return (
    <div className="space-y-3">
      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files?.length) void handleFiles(e.dataTransfer.files);
        }}
        className={`group relative flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed p-6 text-center transition-colors ${
          dragOver ? "border-primary bg-accent/60" : "border-border bg-muted/30 hover:bg-muted/50"
        } ${remaining === 0 ? "pointer-events-none opacity-60" : ""}`}
        aria-label="Add photos of the job"
      >
        <span className="grid h-10 w-10 place-items-center rounded-full bg-primary/10 text-primary">
          <Upload className="h-5 w-5" />
        </span>
        <p className="text-sm font-medium text-foreground">
          {remaining === 0 ? "Maximum photos added" : "Tap to upload or drag photos here"}
        </p>
        <p className="text-xs text-muted-foreground">
          Up to {MAX} photos. JPG, PNG, WEBP or HEIC, 8 MB each.
        </p>
        <div className="mt-2 flex flex-wrap justify-center gap-2 sm:hidden">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="rounded-full"
            onClick={(e) => {
              e.stopPropagation();
              cameraRef.current?.click();
            }}
          >
            <Camera className="mr-1 h-4 w-4" /> Use camera
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="rounded-full"
            onClick={(e) => {
              e.stopPropagation();
              inputRef.current?.click();
            }}
          >
            <ImageIcon className="mr-1 h-4 w-4" /> Choose photo
          </Button>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED.join(",")}
          multiple
          className="sr-only"
          onChange={(e) => e.target.files && void handleFiles(e.target.files)}
        />
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="sr-only"
          onChange={(e) => e.target.files && void handleFiles(e.target.files)}
        />
      </div>

      {value.length > 0 && (
        <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {value.map((p) => {
            const progress = uploading[p.id];
            const err = errors[p.id];
            return (
              <li
                key={p.id}
                className="group relative overflow-hidden rounded-xl border border-border bg-card"
              >
                <img
                  src={p.previewUrl}
                  alt=""
                  className="aspect-square w-full object-cover"
                  loading="lazy"
                />
                {progress !== undefined && progress < 100 && (
                  <div className="absolute inset-x-0 bottom-0 flex items-center gap-2 bg-background/85 px-2 py-1.5 text-[10px] text-muted-foreground backdrop-blur">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <div className="h-1 flex-1 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full bg-primary transition-all"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                )}
                {err && (
                  <div className="absolute inset-x-0 bottom-0 flex items-center gap-1 bg-destructive/90 px-2 py-1 text-[10px] text-destructive-foreground">
                    <AlertCircle className="h-3 w-3" /> {err}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => remove(p.id)}
                  className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-background/90 text-foreground shadow-sm transition hover:bg-background"
                  aria-label={`Remove ${p.name}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {Object.entries(errors)
        .filter(([k]) => !value.find((p) => p.id === k))
        .map(([k, msg]) => (
          <p key={k} role="alert" className="text-xs text-destructive">
            <AlertCircle className="mr-1 inline h-3 w-3" />
            {k}: {msg}
          </p>
        ))}
    </div>
  );
}
